import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CaretUpDownIcon, CheckIcon, XIcon } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "@/features/utils/i18n";

export interface SearchableSelectItem {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any; // Store original data for access
}

interface NoneOption {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
}

interface SearchableSelectProps {
  variant?: "button" | "select";

  // Data
  items: SearchableSelectItem[];
  value: string | string[];
  onChange: (value: string | string[]) => void;

  // Options
  multiple?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  isLoading?: boolean;
  disabled?: boolean;

  // Special options
  allowNone?: boolean;
  noneOption?: NoneOption;

  // Multi-select features
  showBadges?: boolean;
  showSelectAll?: boolean;

  // Display
  popoverWidth?: string;
  align?: "start" | "center" | "end";

  // Search
  onSearchChange?: (search: string) => void;
}

export function SearchableSelect({
  variant,
  items,
  value,
  onChange,
  multiple = false,
  placeholder,
  searchPlaceholder,
  emptyText,
  isLoading = false,
  disabled = false,
  allowNone = false,
  noneOption,
  showBadges = false,
  showSelectAll = false,
  popoverWidth = "w-[400px]",
  align = "start",
  onSearchChange,
}: SearchableSelectProps) {
  const { t } = useTranslation();
  placeholder ??= t("common.select.placeholder");
  searchPlaceholder ??= t("common.select.search");
  emptyText ??= t("common.select.empty");
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const valueArray = Array.isArray(value) ? value : value ? [value] : [];
  const isNoneSelected = allowNone && valueArray.length === 0;

  const handleSelect = (itemId: string) => {
    if (itemId === noneOption?.id) {
      onChange(multiple ? [] : "");
      if (!multiple) setOpen(false);
      return;
    }

    if (multiple) {
      const newValue = valueArray.includes(itemId)
        ? valueArray.filter((id) => id !== itemId)
        : [...valueArray, itemId];
      onChange(newValue);
    } else {
      onChange(itemId);
      setOpen(false);
    }
  };

  const selectAll = () => {
    if (multiple) {
      onChange(items.map((item) => item.id));
    }
  };

  const deselectAll = () => {
    if (multiple) {
      onChange([]);
    }
  };

  const getDisplayText = () => {
    if (isNoneSelected && noneOption) {
      return noneOption.label;
    }
    if (valueArray.length === 0) {
      return placeholder;
    }
    if (valueArray.length === 1) {
      const item = items.find((i) => i.id === valueArray[0]);
      return item?.label || placeholder;
    }
    return t("common.select.selectedCount", { count: valueArray.length });
  };

  const removeItem = (itemId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (multiple) {
      onChange(valueArray.filter((id) => id !== itemId));
    }
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={twMerge(
              "w-full justify-between",
              variant === "select" && "rounded-sm bg-transparent font-normal",
            )}
            disabled={disabled}
          >
            <span className="truncate">{getDisplayText()}</span>
            <CaretUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={`${popoverWidth} p-0`} align={align}>
          <Command>
            <CommandInput 
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={(value) => {
                setSearchValue(value);
                onSearchChange?.(value);
              }}
            />
            <CommandList>
              <CommandEmpty>
                {isLoading ? t("common.loading") : emptyText}
              </CommandEmpty>
              <CommandGroup>
                {showSelectAll && multiple && (
                  <div className="flex items-center gap-2 px-2 py-2 border-b">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={selectAll}
                    >
                      <CheckIcon className="h-3 w-3 mr-1" />
                      {t("common.select.all")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={deselectAll}
                    >
                      <XIcon className="h-3 w-3 mr-1" />
                      {t("common.select.none")}
                    </Button>
                  </div>
                )}
                {allowNone && noneOption && (
                  <CommandItem
                    value={noneOption.id}
                    onSelect={() => handleSelect(noneOption.id)}
                    className="gap-2"
                  >
                    {multiple && (
                      <Checkbox
                        checked={isNoneSelected}
                        className="pointer-events-none"
                      />
                    )}
                    <div className="flex items-center gap-2 flex-1">
                      {noneOption.icon && (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                          {noneOption.icon}
                        </div>
                      )}
                      <div className="flex flex-col">
                        <span className="font-medium">{noneOption.label}</span>
                        {noneOption.description && (
                          <span className="text-xs text-muted-foreground">
                            {noneOption.description}
                          </span>
                        )}
                      </div>
                    </div>
                    {!multiple && isNoneSelected && (
                      <CheckIcon className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                )}
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.description || ""}`}
                    onSelect={() => handleSelect(item.id)}
                    className="gap-2"
                  >
                    {multiple && (
                      <Checkbox
                        checked={valueArray.includes(item.id)}
                        className="pointer-events-none"
                      />
                    )}
                    <div className="flex items-center gap-2 flex-1 min-w-0 dark:hover:text-white">
                      {item.icon && (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 shrink-0">
                          {item.icon}
                        </div>
                      )}
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="font-medium truncate">
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="text-xs text-muted-foreground truncate">
                            {item.description}
                          </span>
                        )}
                      </div>
                    </div>
                    {!multiple && valueArray.includes(item.id) && (
                      <CheckIcon className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {showBadges && multiple && valueArray.length > 0 && !isNoneSelected && (
        <div className="flex flex-wrap gap-1">
          {valueArray.map((itemId) => {
            const item = items.find((i) => i.id === itemId);
            if (!item) return null;
            return (
              <Badge
                key={itemId}
                variant="secondary"
                className="gap-1 pr-1 pl-1.5"
              >
                {item.icon && (
                  <div className="flex items-center -ml-1">{item.icon}</div>
                )}
                <span className="text-xs">{item.label}</span>
                <button
                  type="button"
                  onClick={(e) => removeItem(itemId, e)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <span className="sr-only">
                    {t("common.select.remove", { label: item.label })}
                  </span>
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
